import { TeambinderAiApiService, TeambinderApiService } from '@src/api';
import type { AddAiRealtimeKeywordPayload } from '@src/api';
import { IcoAdd, IcoClose, IcoCloseCircle } from '@src/assets/svg';
import { Input, Modal, Select, message } from 'antd';
import { useState } from 'react';
import { useNavigate, useNavigation } from 'react-router-dom';
import AddRealtimeKeywordDescModal from './AddRealtimeKeywordDescModal';
import { useQuery } from '@tanstack/react-query';
import useKeywordDefinitionChecker from '@src/hooks/useKeywordDefinitionChecker';

const { info } = Modal;

type KeywordCategory = NonNullable<
  AddAiRealtimeKeywordPayload['keywordCategory']
>;

const KEYWORD_CATEGORIES: { value: KeywordCategory; label: string }[] = [
  { value: 'SERVICE', label: '서비스' },
  { value: 'BRAND', label: '브랜드' },
  { value: 'COMPANY', label: '회사' },
  { value: 'PRODUCT', label: '제품' },
  { value: 'PERSON', label: '인물' },
  { value: 'LOCATION', label: '장소' },
];

interface Prop {
  onClose: () => void;
  onSuccess?: (keywordId: string) => void;
  parentKeywordId?: string;
  defaultFolderId?: string | null;
  folderOptions?: {
    folderId: string;
    name: string;
  }[];
  showFolderSelect?: boolean;
  defaultData?: {
    keywordId: string;
    keyword: string;
    synonyms: string[];
    exclusions: string[];
    keywordCategory?: KeywordCategory;
  };
}
const RealtimeKeywordModal = ({
  onClose,
  onSuccess,
  parentKeywordId,
  defaultFolderId = null,
  folderOptions = [],
  showFolderSelect = true,
  defaultData,
}: Prop) => {
  const [keyword, setKeyword] = useState(defaultData?.keyword || '');
  const shouldShowFolderSelect =
    showFolderSelect && !defaultData && !parentKeywordId;
  const { data: fetchedFolderOptions = [] } = useQuery({
    queryKey: ['realtime-keyword-folders'],
    queryFn: async () => {
      const result = await TeambinderAiApiService.getAiRealtimeKeywordTree();
      return (
        result.data?.folders
          ?.filter((folder) => folder.folderId)
          .map((folder) => ({
            folderId: folder.folderId!,
            name: folder.name || '이름 없는 폴더',
          })) ?? []
      );
    },
    enabled: shouldShowFolderSelect && folderOptions.length === 0,
    suspense: false,
  });
  const effectiveFolderOptions =
    folderOptions.length > 0 ? folderOptions : fetchedFolderOptions;
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(
    defaultFolderId,
  );

  const [synonym, setSynonym] = useState('');
  const [synonyms, setSynonyms] = useState<string[]>(
    defaultData?.synonyms || [],
  );

  const [exclusion, setExclusion] = useState('');
  const [exclusions, setExclusions] = useState<string[]>(
    defaultData?.exclusions || [],
  );

  const [keywordCategory, setKeywordCategory] = useState<KeywordCategory | ''>(
    defaultData?.keywordCategory || '',
  );

  const navigate = useNavigate();

  const hasCommaInKeyword = keyword.includes(',');

  const [isDescModalOpen, setIsDescModalOpen] = useState(false);
  
  const { addKeywordToCheck } = useKeywordDefinitionChecker();

  const addSynonymOrExclusion = (
    type: 'synonym' | 'exclusion',
    keyword: string,
  ) => {
    if (type === 'synonym') {
      if (synonyms.length >= 10) {
        message.warning('동의어는 최대 10개까지 등록할 수 있습니다.');
        return;
      }
      const str = synonym.trim();
      setSynonym('');
      if (str && !synonyms.includes(str)) {
        setSynonyms([...synonyms, keyword]);
      }
    } else {
      const str = exclusion.trim();
      setExclusion('');
      if (str && !exclusions.includes(str)) {
        setExclusions([...exclusions, keyword]);
      }
    }
  };

  const removeSynonym = (synonym: string) => {
    setSynonyms(synonyms.filter((s) => s !== synonym));
  };

  const removeExclusion = (exclusion: string) => {
    setExclusions(exclusions.filter((e) => e !== exclusion));
  };

  const handleAdd = async () => {
    if (keyword.trim() === '') {
      return;
    }

    if (hasCommaInKeyword) {
      return;
    }

    if (!keywordCategory) {
      info({
        title: '키워드 특성을 선택해 주세요.',
        okText: '확인',
      });
      return;
    }

    if (defaultData) {
      if (parentKeywordId) {
        const res = await TeambinderAiApiService.modifyCompetitorKeyword({
          competitorId: defaultData.keywordId,
          requestBody: {
            keyword: keyword,
            synonyms,
            excludeKeywords: exclusions,
            keywordCategory: keywordCategory as KeywordCategory,
          },
        });

        info({
          title: '수정되었습니다.',
          okText: '확인',
          onOk: () => {
            onClose();
          },
        });
      } else {
        const res = await TeambinderAiApiService.modifyAiRealtimeKeyword({
          keywordId: defaultData.keywordId,
          requestBody: {
            keyword: keyword,
            synonyms,
            excludeKeywords: exclusions,
            keywordCategory: keywordCategory as KeywordCategory,
          },
        });

        info({
          title: '수정되었습니다.',
          okText: '확인',
          onOk: () => {
            if (onSuccess) {
              onSuccess(defaultData.keywordId);
            }
            onClose();
          },
        });
      }
    } else {
      let keywordId = '';
      if (parentKeywordId) {
        const res = await TeambinderAiApiService.addCompetitorKeyword({
          keywordId: parentKeywordId,
          requestBody: {
            keyword: keyword,
            synonyms,
            excludeKeywords: exclusions,
            keywordCategory: keywordCategory as KeywordCategory,
          },
        });
        keywordId = res.data!;

        info({
          title: '경쟁사 키워드가 추가되었습니다.',
          okText: '확인',
          onOk: () => {
            if (onSuccess) {
              onSuccess(res.data!);
            }
            onClose();
          },
        });
      } else {
        const res = await TeambinderAiApiService.addAiRealtimeKeyword({
          requestBody: {
            keyword: keyword,
            synonyms,
            excludeKeywords: exclusions,
            keywordCategory: keywordCategory as KeywordCategory,
            folderId:
              (shouldShowFolderSelect ? selectedFolderId : defaultFolderId) ??
              undefined,
          },
        });
        keywordId = res.data!;

        // 키워드 정의 확인을 위해 로컬 스토리지에 저장
        addKeywordToCheck(keywordId, keyword);

        info({
          title: '🔊 안내',
          content: (
            <div style={{ marginTop: '20px' }}>
              <p style={{ fontSize: '16px', marginBottom: '20px' }}>
                키워드 등록 후 데이터 확인 가능 시간은 아래와 같습니다!
              </p>
              <ul style={{ paddingLeft: '18px', lineHeight: '1.8' }}>
                <li>
                  <strong>AI실시간 감지</strong> : 키워드 등록 후{' '}
                  <strong>10~20분 뒤</strong> 바로 데이터 확인 가능
                </li>
                <li>
                  <strong>AI인사이트</strong> : 키워드 등록 후{' '}
                  <strong>하루(24시간) 뒤</strong> 데이터 확인 가능
                </li>
                <li>
                  <strong>AI종합리포트</strong> : 키워드 등록 후{' '}
                  <strong>하루(24시간) 뒤</strong> 데이터 확인 가능
                </li>
              </ul>
            </div>
          ),
          okText: '확인',
          width: 600,
          onOk: () => {
            if (onSuccess && keywordId) {
              onSuccess(keywordId);
            }
            onClose();
          },
        });
        return;
      }
      if (onSuccess && keywordId) {
        onSuccess(keywordId);
      }
    }
  };

  return (
    <Modal
      open
      onCancel={onClose}
      footer={null}
      title={
        defaultData
          ? '키워드 수정'
          : parentKeywordId
          ? '경쟁사 키워드 추가'
          : '키워드 추가'
      }
    >
      <div className="addRealtimeKeywordModal">
        <div className="inputGroup">
          <label>키워드</label>
          <Input
            placeholder="키워드를 입력해 주세요."
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            status={hasCommaInKeyword ? 'error' : undefined}
          />
          {hasCommaInKeyword && (
            <div
              style={{
                color: '#ff4d4f',
                fontSize: '12px',
                marginTop: '4px',
              }}
            >
              콤마(,) 없이 키워드 하나만 입력해 주세요.
            </div>
          )}
        </div>
        {shouldShowFolderSelect && (
          <div className="inputGroup">
            <label>폴더</label>
            <Select
              value={selectedFolderId ?? 'unassigned'}
              onChange={(value) =>
                setSelectedFolderId(value === 'unassigned' ? null : value)
              }
              style={{ width: '100%' }}
            >
              <Select.Option value="unassigned">폴더 없음</Select.Option>
              {effectiveFolderOptions.map((folder) => (
                <Select.Option key={folder.folderId} value={folder.folderId}>
                  {folder.name}
                </Select.Option>
              ))}
            </Select>
          </div>
        )}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '16px',
          }}
        >
          <label style={{ margin: 0, whiteSpace: 'nowrap', fontWeight: 600 }}>
            키워드 특성
          </label>
          <div style={{ display: 'flex', gap: '8px' }}>
            {KEYWORD_CATEGORIES.map((item) => (
              <button
                key={item.value}
                type="button"
                onClick={() => setKeywordCategory(item.value)}
                style={{
                  padding: '4px 12px',
                  border: '1px solid #d9d9d9',
                  borderRadius: '6px',
                  background:
                    keywordCategory === item.value ? '#4403B5' : '#fafafa',
                  color: keywordCategory === item.value ? '#fff' : '#666',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: keywordCategory === item.value ? '500' : '400',
                  transition: 'all 0.2s',
                  boxShadow:
                    keywordCategory === item.value
                      ? '0 2px 4px rgba(68, 3, 181, 0.2)'
                      : 'none',
                }}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
        <div className="inputGroup">
          <div className="option">
            <label>세부옵션(선택사항)</label>
            <button onClick={() => setIsDescModalOpen(true)}>세부설명</button>
          </div>
          <label>동의어</label>
          <div className="synonymInput">
            <Input
              placeholder="동의어를 입력하세요."
              value={synonym}
              onChange={(e) => setSynonym(e.target.value)}
              onKeyDown={(e) => {
                e.key === 'Enter' &&
                  !e.nativeEvent.isComposing &&
                  addSynonymOrExclusion('synonym', synonym);
              }}
            />
            <button
              className="addSynonym"
              onClick={() => {
                addSynonymOrExclusion('synonym', synonym);
              }}
            >
              <IcoAdd width={24} height={24} fill="#4403B5" />
            </button>
          </div>
          <div className="synonymList">
            {synonyms.map((synonym) => (
              <span className="synonym" key={synonym}>
                {synonym}
                <IcoClose
                  fontSize={20}
                  style={{ cursor: 'pointer' }}
                  onClick={() => removeSynonym(synonym)}
                />
              </span>
            ))}
          </div>
        </div>
        <div className="inputGroup">
          <label>제외어</label>
          <div className="exclusionInput">
            <Input
              placeholder="제외어를 입력하세요."
              value={exclusion}
              onChange={(e) => setExclusion(e.target.value)}
              onKeyDown={(e) => {
                e.key === 'Enter' &&
                  !e.nativeEvent.isComposing &&
                  addSynonymOrExclusion('exclusion', exclusion);
              }}
            />
            <button
              className="addExclusion"
              onClick={() => {
                addSynonymOrExclusion('exclusion', exclusion);
              }}
            >
              <IcoAdd width={24} height={24} fill="#4403B5" />
            </button>
          </div>
          <div className="exclusionList">
            {exclusions.map((exclusion) => (
              <span
                className="exclusion"
                key={exclusion}
                onClick={() => removeExclusion(exclusion)}
              >
                {exclusion}
                <IcoClose
                  fontSize={20}
                  style={{ cursor: 'pointer' }}
                  onClick={() => removeExclusion(exclusion)}
                />
              </span>
            ))}
          </div>
        </div>
        <div>
          <button className="add" onClick={handleAdd}>
            {defaultData
              ? '키워드 수정'
              : parentKeywordId
              ? '경쟁사 키워드 추가'
              : '키워드 추가'}
          </button>
        </div>
      </div>
      {isDescModalOpen && (
        <AddRealtimeKeywordDescModal
          onClose={() => setIsDescModalOpen(false)}
        />
      )}
    </Modal>
  );
};

export default RealtimeKeywordModal;
